import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IButtonStyles, ITheme } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { concatStyleSets, memoizeFunction } from "@fluentui/react";

import type { AppSplitButtonInternalProps } from "@1js/acui-button";
import { AppSplitButton, getActionSplitButtonStyles } from "@1js/acui-button";
import { isV9ButtonEnabled } from "@1js/acui-experiments";
import { MeasuredContext } from "@1js/acui-resize-group";
import { shouldShadowRender } from "@1js/acui-shadow-rendering";
import { AppThemeConsumer } from "@1js/acui-theme";
import { AppContext } from "@1js/acui-context";

import { animatePropsChanges } from "../../components/PropsAnimator/PropsAnimator";
import { getOnRenderText } from "../onRenderText";
import type { UseLegacyRibbonButtonProps } from "../RibbonButton";
import { getRibbonButtonLabelTransitionStyles } from "../RibbonButton/RibbonButton.styles";
import type { HideLabelAnimationProps } from "../RibbonButton/RibbonButtonHideLabelAnimation";
import { getHideLabelAnimationOptions } from "../RibbonButton/RibbonButtonHideLabelAnimation";
import type { RibbonControlInternalProps } from "../RibbonControlProps";
import { RibbonSplitButton as RibbonSplitButtonNext } from "./next";
import {
  useV9RibbonControlsEnabled,
  useNewV9RibbonMenuEnabled,
} from "../../../../hooks";

export type RibbonSplitButtonInternalProps = AppSplitButtonInternalProps &
  RibbonControlInternalProps &
  UseLegacyRibbonButtonProps &
  HideLabelAnimationProps;

declare let __Delay_FluentV8__: boolean;

const V8RibbonAppSplitButton =
  typeof __Delay_FluentV8__ === "undefined" || !__Delay_FluentV8__
    ? AppSplitButton
    : React.lazy(
        () =>
          import(
            /*webpackChunkName: "commonV8"*/ "@1js/acui-button/lib/AppSplitButton"
          )
      );

const getStyles = memoizeFunction(
  (
    transitionStyles: IButtonStyles,
    splitButtonStyles?: IButtonStyles,
    customStyles?: IButtonStyles
  ) => {
    return concatStyleSets(splitButtonStyles, transitionStyles, customStyles);
  }
);

class BaseRibbonSplitButton extends React.PureComponent<
  RibbonSplitButtonInternalProps,
  {}
> {
  public render() {
    const { shadowRenderMenu, styles } = this.props;
    const transitionStyles = getRibbonButtonLabelTransitionStyles(
      this.props.textContainerInitialWidth,
      this.props.textContainerFinalWidth,
      this.props.hideLabel
    );

    return (
      <MeasuredContext.Consumer>
        {({ isMeasured }) => (
          <AppThemeConsumer>
            {(theme: ITheme) => (
              <React.Suspense fallback={<></>}>
                <V8RibbonAppSplitButton
                  {...this.props}
                  styles={getStyles(
                    transitionStyles,
                    getActionSplitButtonStyles(theme),
                    styles
                  )}
                  dataAutomationType={
                    this.props.dataAutomationType || "RibbonSplitButton"
                  }
                  onRenderText={
                    this.props.onRenderText || getOnRenderText(this.props.size)
                  }
                  persistMenu={true}
                  shadowRenderMenu={shouldShadowRender(
                    shadowRenderMenu,
                    isMeasured
                  )}
                  preventCalloutDismissOnScroll={true}
                />
              </React.Suspense>
            )}
          </AppThemeConsumer>
        )}
      </MeasuredContext.Consumer>
    );
  }
}

export const RibbonSplitButtonCurrent = animatePropsChanges(
  getHideLabelAnimationOptions<RibbonSplitButtonInternalProps>()
)(BaseRibbonSplitButton);
RibbonSplitButtonCurrent.displayName = "RibbonSplitButtonCurrent";

export const RibbonSplitButton = React.forwardRef<
  HTMLButtonElement,
  RibbonSplitButtonInternalProps
>((props, ref) => {
  const appContext = React.useContext(AppContext);
  const isV9RibbonControlsEnabled = useV9RibbonControlsEnabled();
  const isV9MenuEnabled = useNewV9RibbonMenuEnabled();
  const isNewButtonEnabled =
    (isV9ButtonEnabled(appContext.isExperimentalFeatureEnabled) ||
      isV9RibbonControlsEnabled) &&
    !props.useLegacyButton;
  const finalProps = {
    ...props,
    useLegacyContextMenu: isV9MenuEnabled ? false : true,
  };
  return isNewButtonEnabled ? (
    <RibbonSplitButtonNext ref={ref} {...finalProps} />
  ) : (
    <RibbonSplitButtonCurrent ref={ref as any} {...props} />
  );
});
RibbonSplitButton.displayName = "RibbonSplitButton";

export type RibbonSplitButton = typeof RibbonSplitButton.prototype;
